import { Upload } from "lucide-react";
import type { DragEvent } from "react";
import { useId, useRef, useState } from "react";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Button } from "@src/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { cn } from "@src/shared/lib/utils";

const DEFAULT_ACCEPTED_FILE_TYPES = ".pdf,.png,.jpg,.jpeg";
const GAP_LIST_TOOLTIP = `A gap list is a payer or value-based care report with open quality gaps for this patient. Drop it here and ${PRODUCT_NAME.singular} turns each gap into agenda items.`;

export interface GapListDropzoneV2Props {
  disabled?: boolean;
  isUploading?: boolean;
  multiple?: boolean;
  acceptedFileTypes?: string;
  onFilesSelected?: (files: File[]) => void;
  /**
   * "default" renders the labeled trigger ("Drop a gap list") used in its
   * own row. "compact" renders an icon-only trigger sized to sit inline
   * inside the agenda composer pill (STITCH-551) — same drag/drop and
   * click-to-pick handling, only the trigger's visible content changes. The
   * accessible name ("Upload gap list") is identical in both variants.
   */
  variant?: "default" | "compact";
}

export function GapListDropzoneV2({
  disabled = false,
  isUploading = false,
  multiple = false,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  variant = "default",
  onFilesSelected,
}: GapListDropzoneV2Props) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerId = useId();
  const hintId = useId();
  const isDisabled = disabled || isUploading;

  const handleFiles = (fileList: FileList | null) => {
    if (isDisabled || !fileList?.length) return;
    const files = Array.from(fileList);
    onFilesSelected?.(multiple ? files : files.slice(0, 1));
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    const relatedTarget = event.relatedTarget as Node | null;

    if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    event.preventDefault();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  return (
    <div className="inline-flex items-center">
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id={triggerId}
              type="button"
              variant="ghost"
              disabled={isDisabled}
              aria-label="Upload gap list"
              aria-describedby={hintId}
              className={cn(
                variant === "compact"
                  ? "size-8 shrink-0 rounded-[8px] p-0 text-muted-foreground shadow-none hover:bg-primary/5 hover:text-primary disabled:opacity-45"
                  : "h-auto gap-1.5 rounded-[8px] border border-dashed border-transparent px-1.5 py-1 text-[11px] font-medium normal-case text-muted-foreground shadow-none hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-45",
                isDragging &&
                  (variant === "compact"
                    ? "bg-primary/10 text-primary ring-1 ring-primary/50"
                    : "border-primary/50 bg-primary/10 text-primary"),
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload
                className={variant === "compact" ? "size-4" : "size-3.5"}
                aria-hidden="true"
              />
              {variant === "compact"
                ? null
                : isUploading
                  ? "Uploading gap list..."
                  : "Drop a gap list"}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px] leading-5">
            {GAP_LIST_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span id={hintId} className="sr-only">
        Drop a PDF, PNG, or JPG gap list file here, or click to open the file
        picker.
      </span>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        disabled={isDisabled}
        multiple={multiple}
        tabIndex={-1}
        aria-label="Gap list file picker"
        className="sr-only"
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );
}
